import { Component, type ReactNode } from 'react';

/**
 * Catches a screen that could not be LOADED, as opposed to one that could not
 * be rendered.
 *
 * Code-splitting a route buys a smaller entry chunk and introduces a failure
 * the console did not have before: a rejected `import()` throws straight
 * through Suspense, and with no boundary above it React unmounts the whole
 * app — a white page, not a broken panel. It happens without anything being
 * wrong with the code: deploy a new build while a tab sits open and the hashed
 * chunk the old page asks for is simply gone. React also caches the rejected
 * promise, so navigating away and back retries nothing.
 *
 * Hence the reload rather than a retry button: the only thing that fixes a
 * missing chunk is fetching the current index.html.
 */
export class ChunkBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error) {
    console.error('[console] screen failed to load', error);
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="p-6 text-center text-sm text-[var(--co-muted)]">
        <p>This screen could not be loaded. It usually means a new version was just released.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] px-4 py-2 text-[var(--co-text)]"
        >
          Reload the page
        </button>
      </div>
    );
  }
}
